import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Modal } from "@/common/components/modal";
import Table from "@/common/components/Table";
import {
  PRODUCT_URL,
  INVENTORY_URL,
  INVENTORY_PRODUCTS_URL,
  INVENTORY_STATS_URL,
  BATCH_INVENTORY_URL,
  BATCH_PRODUCT_URL,
} from "@/common/constants";
import {
  ModalFormWrapper,
  ModalWideWrapper,
  ModalWrapper,
} from "@/common/elements";
import { KeyValueObj } from "@/common/types";
import { useApiCallHandling } from "@/hooks/useApiCallHandling";
import { useAuthDetails } from "@/hooks/useAuthDetails";
import useAxiosCall from "@/hooks/useAxiosCall";
import { Barchart } from "../common/Barchart";
import { barOptions } from "../common/Data";
import {
  getBarchartData,
  getCardDetails,
  getRecentOrderTableData,
  ICardDetails,
  InventoryFilterOptions,
  productInventoryTableHeaders,
  productInventoryTableKeys,
  tableHeadersShort,
  tableKeysShort,
} from "./subs/data";
import { AddInventoryWrapper } from "./subs/style";
import { StatCard } from "./subs/StatCard";
import { PageTitleNavs } from "@/common/components/page-title-navs";
import { Button } from "@/common/components/button";
import { toParamsFormat } from "@/common/utils/general";
import { downloadBase64File } from "@/common/utils";
import useQuery from "@/hooks/useQuery";
import { PageSkeletonLoader } from "@/common/components/skeleton/page-skeleton";
import { ActionPopup } from "@/common/components/action-popup";
import { usePositioning } from "@/hooks/usePositioning";
import { ProductFactory } from "../common/product-factory";
import FileUpload from "./batch/fileUploader";
import PreviewInventory from "./batch/preview";
import { Select } from "@/common/components/selectInput";
import { useNotification } from "@/common/components/Notifications/NotificationProvider";
import { InventoryStep1 } from "./subs/InventoryStep1";
import { InventoryStep2 } from "./subs/InventoryStep2";
import { IInventoryNode } from "./subs/inventoryTree";
import { toSubmitTree } from "./subs/serialAccelerators";

export interface IAddInventoryProps {}
export interface IInventoryHierarchy {
  level: string;
  packaging_type: string;
  containment_quantity: number;
}

export function AddInventory(_: IAddInventoryProps) {
  //STATES
  const [modalTitle, setModalTitle] = useState("");
  const [modalSubtitle] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [stage, setStage] = useState("inventory-step1");
  const [retry, setRetry] = useState(0);
  const [inventories, setInventories] = useState<KeyValueObj[]>([]);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [, setFormValues] = useState<KeyValueObj>({});
  const [tableData, setTableData] = useState<any[]>([]);
  const [showInventoryOptions, setShowInventoryOptions] = useState(false);
  const [productType, setProductType] = useState("");
  const [addMore, setAddMore] = useState(false);
  const [openBatchUpload, setOpenBatchUpload] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [step, setStep] = useState<"form" | "preview">("form");
  // MED-646 serialization: per-product template download + header-mismatch confirm
  const [templateLoading, setTemplateLoading] = useState(false);
  const [headerMismatch, setHeaderMismatch] = useState<KeyValueObj | null>(
    null,
  );
  const [page, __setPage] = useState(1);
  const [perPage, __setPerPage] = useState(25);
  const [searching, setSearching] = useState(false);
  const [, setPagCount] = useState(1);

  // MED-649: complete-batch single-upload flow (stages "inventory-step1"/"inventory-step2")
  const [inventoryProduct, setInventoryProduct] = useState<KeyValueObj | null>(
    null,
  );
  const [inventoryStep1Values, setInventoryStep1Values] = useState<KeyValueObj>(
    {},
  );
  const [inventorySubmitLoading, setInventorySubmitLoading] = useState(false);
  const [serverErrorBySerial, setServerErrorBySerial] = useState<
    Record<string, string>
  >({});

  // ===== HOOKES =====
  const [
    getInventories,
    inventoriesFetched,
    inventoriesError,
    inventoriesLoading,
  ] = useAxiosCall();

  const { data: inventoryMetrics, loading } = useQuery(INVENTORY_STATS_URL);
  const apiCall = useApiCallHandling();
  const [searchParams] = useSearchParams();
  const { companyType } = useAuthDetails();
  // ********* Hooks ***********
  const [setPosition, position] = usePositioning();

  const [BatchformData, setBatchFormData] = useState<KeyValueObj>({});

  const [getProducts, productsFetched, productsError, productsLoading] =
    useAxiosCall();
  const { data: BatchInvertoryData } = useQuery(BATCH_PRODUCT_URL);
  const notify = useNotification();
  const navigate = useNavigate();

  const productOptions = useMemo<KeyValueObj[]>(() => {
    return (
      productsFetched?.map((product: KeyValueObj) => ({
        id: product.id,
        title: `${product.atc_code?.readable_name}(${product.brand_name}) - ${
          product?.strength
        }${product?.strength_unit === "grams" ? "mg" : "g"}`,
      })) ?? []
    );
  }, [productsFetched]);

  //FETCHING PAGE LOAD DATA
  useEffect(() => {
    getProducts({
      method: "GET",
      url: PRODUCT_URL + "?status=approved",
    });
    getInventories({
      method: "GET",
      url: INVENTORY_PRODUCTS_URL,
    });
  }, [retry]);

  //HANDLING INVENTORY TABLE
  useEffect(() => {
    inventoriesFetched && setInventories(inventoriesFetched);
  }, [inventoriesFetched]);

  // MED-652: one row per product, aggregated across that product's batches.
  useEffect(() => {
    const inventoryTable =
      inventories.length > 0 &&
      inventories.map((product: KeyValueObj) => ({
        product_name: {
          value: product?.product_brand_name || product?.product_name,
          type: "string",
        },
        sku: {
          value: product?.sku,
          type: "string",
        },
        batch_count: {
          value: product?.batch_count,
          type: "string",
        },
        total_quantity: {
          value: product?.total_quantity,
          type: "string",
        },
        id: product?.product_id,
      }));

    inventoryTable && setTableData(inventoryTable);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventories]);

  useEffect(() => {
    if (searchParams.get("new") === "true" && !productsLoading) {
      setShowModal(true);
      setModalTitle("Add Inventory");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productsLoading]);

  //FUNCTIONS

  function handleProductTypeSelected(type: string) {
    setProductType(type);
    if (type === "single_update") {
      setShowModal(true);
      setModalTitle("Add Inventory — Step 1 of 2");
      setStage("inventory-step1");
    }

    if (type === "bulk_upload") {
      setShowModal(false);
      setOpenBatchUpload(true);
    }
    setShowInventoryOptions(false);
  }

  const InvertoryTypeActions = [
    {
      label: "Single Upload",
      action: () => handleProductTypeSelected("single_update"),
    },
    {
      label: "Bulk Upload",
      action: () => handleProductTypeSelected("bulk_upload"),
    },
  ];

  function handleAddInventory(e: any) {
    setPosition(e);
    setShowInventoryOptions(true);
  }

  // MED-649: Step 1 -> Step 2 of the complete-batch inventory flow.
  function onInventoryStep1Continue(values: KeyValueObj, product: KeyValueObj) {
    setInventoryStep1Values(values);
    setInventoryProduct(product);
    setServerErrorBySerial({});
    setModalTitle("Add Inventory — Step 2 of 2");
    setStage("inventory-step2");
  }

  function onInventoryStep2Back() {
    setModalTitle("Add Inventory — Step 1 of 2");
    setStage("inventory-step1");
  }

  function resetInventoryFlow() {
    setInventoryProduct(null);
    setInventoryStep1Values({});
    setServerErrorBySerial({});
    setStage("inventory-step1");
  }

  // MED-649 §3.3 bulk-upload nudge: hand off to the existing bulk-upload
  // modal, pre-selecting the same product the actor was already on.
  function onSwitchToBulkUpload(productId: string) {
    setShowModal(false);
    resetInventoryFlow();
    setBatchFormData({ product_id: productId });
    setOpenBatchUpload(true);
  }

  async function onInventorySubmit(tree: IInventoryNode) {
    setInventorySubmitLoading(true);
    const payload = {
      product_id: inventoryProduct?.id,
      ...inventoryStep1Values,
      inventory: toSubmitTree(tree),
    };

    const data = await apiCall(
      INVENTORY_URL,
      "POST",
      payload,
      () => {},
      (err: any) => {
        const apiErrors: KeyValueObj[] =
          err?.response?.data?.data?.errors || [];
        const bySerial: Record<string, string> = {};
        apiErrors.forEach((e) => {
          if (e.serial) bySerial[e.serial] = e.message;
        });
        setServerErrorBySerial(bySerial);
      },
      () => {
        setInventorySubmitLoading(false);
      },
      { dontNotifyOnSucess: true },
    );

    if (data?.inventory) {
      // Ticket §Basic Flow — "Actor receives confirmation with a transaction ID."
      notify({
        type: "SUCCESS",
        message: `Inventory registered — transaction ID ${data.transaction_id}`,
      });
      setInventories([data.inventory, ...inventories]);
      resetInventoryFlow();
      setShowModal(false);
    }
  }

  // ************** filter batch data ************
  const selectedProduct = BatchInvertoryData?.find(
    (item: any) => item.id === BatchformData?.product_id,
  );

  // MED-646: download the per-product serialization template (dynamic per-level
  // columns) generated by the backend, replacing the static xlsx link.
  async function downloadSerializationTemplate(productId?: string) {
    if (!productId) return;
    setTemplateLoading(true);
    await apiCall(
      `${PRODUCT_URL}/${productId}/serialization-template`,
      "GET",
      {},
      (data: KeyValueObj) => {
        downloadBase64File(data.file_base64, data.filename, data.content_type);
      },
      () => {},
      () => {
        setTemplateLoading(false);
      },
    );
  }

  async function onBatchSubmit(confirmHeaderMismatch = false) {
    setSubmitLoading(true);
    if (!confirmHeaderMismatch) setHeaderMismatch(null);
    const formData = new FormData();
    // Append form fields
    Object.entries(BatchformData).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, String(value));
      }
    });

    // Append extra data
    formData.append("product_name", selectedProduct?.name ?? "");

    // Append file
    if (selectedFile) {
      formData.append("file", selectedFile);
    }

    // MED-646 §4.3: header names differed but positions were valid — re-POST to
    // override after the user confirms.
    if (confirmHeaderMismatch)
      formData.append("confirm_header_mismatch", "true");

    await apiCall(
      BATCH_INVENTORY_URL,
      "POST",
      formData,
      () => {
        setFormValues({});
        setHeaderMismatch(null);
        setShowModal(false);
        setOpenBatchUpload(false);
        setRetry((prev: number) => prev + 1); // refresh the inventory table
      },
      (err: any) => {
        const body = err?.response?.data;
        if (err?.response?.status === 409 && body?.data?.header_mismatch) {
          // positionally valid but names differ — offer confirm-to-override
          setHeaderMismatch(body.data);
        } else {
          notify({
            type: "ERROR",
            message: body?.message || "Upload failed",
          });
        }
      },
      () => {
        setSubmitLoading(false);
      },
      { dontNotifyonFail: true },
    );
  }

  function onCloseModal() {
    setShowModal(false);
    setInventoryProduct(null);
    setInventoryStep1Values({});
    setServerErrorBySerial({});
  }

  function CloseBatuploadModal() {
    setStep("form");
    setSelectedFile(null);
    setHeaderMismatch(null);
    setOpenBatchUpload(false);
  }

  if (loading) return <PageSkeletonLoader />;

  //  ************** Batch upload functions ***************
  const handleExternalUpdate = (name: string, value: any) => {
    setBatchFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ================ handling search and filters ============
  const INVENTORIES_URL = INVENTORY_PRODUCTS_URL;

  /*
  // Previous search implementation kept here for reference
  function buildInventoryQueryParams(filters: KeyValueObj = {}) {
    const mergedFilters: KeyValueObj = {
      ...filters,
      page,
      per_page: perPage,
    };

    if (filters.search_term) {
      mergedFilters.search_term = filters.search_term;
    }

    const cleanedFilters = Object.entries(mergedFilters).reduce<KeyValueObj>(
      (acc, [key, value]: [string, any]) => {
        const isEmptyValue =
          value === undefined ||
          value === null ||
          (typeof value === "string" && value.trim() === "");

        if (isEmptyValue) {
          return acc;
        }

        acc[key] = value;
        return acc;
      },
      {},
    );

    return toParamsFormat(cleanedFilters);
  }

  function updateTableData(queryParams: string) {
    apiCall(
      `${INVENTORY_URL}${queryParams}`,
      "GET",
      {},
      (data) => {
        const inventoryTable = (data || [])
          .slice()
          .reverse()
          .map((inventory: (typeof inventoriesFetched)[0]) => ({
            name: {
              value: inventory?.product?.brand_name,
              type: "string",
            },

            unit: {
              value: inventory?.product?.form,
              type: "string",
            },
            strength: {
              type: "string",
              value: `${inventory?.product?.strength}${
                inventory?.product?.strength_unit === "milligrams" ? "mg" : "g"
              }`,
            },
            storage: {
              value: inventory?.product.storage_requirement,
              type: "string",
            },

            quantity: {
              value: inventory?.quantity_in_stock,
              type: "string",
            },
            minLevel: {
              value: inventory?.reorder_level,
              type: "string",
            },
            supply: {
              value: inventory?.quantity_sold,
              type: "string",
            },
            selling_price: {
              value: formatToNaira(inventory?.selling_price_per_unit),
              type: "string",
            },
            id: inventory?.id,
          }));

        inventoryTable && setTableData(inventoryTable);
      },
      () => {},
      () => {},
      { dontNotifyOnSucess: true },
    );
  }

  function handleSearch(value: string) {
    const trimmedValue = value?.trim();
    const nextFilters = { ...inventoryFilters };

    if (trimmedValue) {
      nextFilters.search_term = trimmedValue;
    } else {
      delete nextFilters.search_term;
    }

    setInventoryFilters(nextFilters);
    updateTableData(buildInventoryQueryParams(nextFilters));
  }

  function handleOrderFilter(values: KeyValueObj) {
    const normalizedValues = Object.entries(values).reduce<KeyValueObj>(
      (acc, [key, value]) => {
        if (value === undefined || value === null || value === "") {
          return acc;
        }

        acc[key] = value;
        return acc;
      },
      {},
    );

    const nextFilters = { ...inventoryFilters, ...normalizedValues };

    setInventoryFilters(nextFilters);
    updateTableData(buildInventoryQueryParams(nextFilters));
  }
  */

  function updateTableData(queryParams: string) {
    setSearching(true);

    apiCall(
      `${INVENTORIES_URL}${queryParams}`,
      "GET",
      {},
      (data: any, metaData: any) => {
        setInventories(data);

        if (typeof metaData?.total_pages === "number") {
          setPagCount(metaData.total_pages);
        }

        if (data?.length === 0) {
          setTableData([]);
        }
      },
      (err) => console.log(err),
      () => setSearching(false),
      {
        dontNotifyOnSucess: true,
        dontNotifyonFail: true,
      },
    );
  }

  function handleSearch(value: string) {
    const values = {
      page,
      per_page: perPage,
      search_term: value,
    };

    const params = toParamsFormat(values);
    updateTableData(params);
  }

  function handleInventoryFilter(values: KeyValueObj) {
    values.page = page;
    values.per_page = perPage;

    const params = toParamsFormat(values);
    updateTableData(params);
  }
  return (
    <>
      <AddInventoryWrapper>
        <PageTitleNavs
          title="Inventories"
          backLink="#"
          bcrumbs={[{ title: "Inventories", link: "#" }]}
        />
        <ProductFactory
          open={addMore}
          close={() => setAddMore(false)}
          productType={productType}
        />{" "}
        <div className="stats-wrapper">
          <div className="cards-wrapper">
            {getCardDetails(inventoryMetrics?.metrics)?.map(
              (card: ICardDetails, idx: number) => (
                <StatCard
                  key={idx}
                  title={card.title}
                  value={card.value}
                  imageUrl={card.imageUrl}
                  bg={card.bg}
                />
              ),
            )}
          </div>

          <div className="inventories ">
            <div className="controls">
              {(companyType?.toLowerCase() === "manufacturer" ||
                companyType?.toLowerCase() === "importer") && (
                <>
                  <Button onClick={handleAddInventory}>+ Add Inventory</Button>
                </>
              )}
            </div>
            <div className="table-wrapper">
              <Table
                tableHeaders={productInventoryTableHeaders}
                tableData={tableData}
                actionsData={[]}
                tableKeys={productInventoryTableKeys}
                full
                rowLineColor="light"
                loading={inventoriesLoading || searching}
                errorLoading={inventoriesError?.message?.length > 0}
                isSearching={searching}
                onRetry={() => setRetry((prev) => prev + 1)}
                clickable
                onTRClick={(id) => navigate(`/dashboard/inventories/${id}`)}
                filterInputs={InventoryFilterOptions}
                handleFilter={handleInventoryFilter}
                handleSearch={handleSearch}
              />
            </div>
          </div>

          <div className="mid-stats">
            <div className="bar">
              <h2 className="sub-title">Sales</h2>
              <Barchart
                chartData={getBarchartData(
                  inventoryMetrics?.sales_statistics?.monthly_breakdown,
                )}
                options={barOptions}
              />
            </div>
            <div className="short-table">
              <h2 className="sub-title">Recent Orders</h2>
              <Table
                tableHeaders={tableHeadersShort}
                tableData={getRecentOrderTableData(
                  inventoryMetrics?.recent_orders,
                )}
                actionsData={[]}
                tableKeys={tableKeysShort}
                full={false}
                rowLineColor="light"
              />
            </div>
          </div>
        </div>
        <Modal
          title={modalTitle}
          subTitle={modalSubtitle}
          isOpen={showModal}
          close={onCloseModal}
        >
          <ModalWrapper short={false}>
            {/* MED-649: complete-batch single upload, step 1 (batch details) */}
            <ModalFormWrapper show={stage === "inventory-step1"}>
              <InventoryStep1
                productOptions={productOptions}
                productsFetched={productsFetched}
                productsLoading={productsLoading}
                productsError={productsError}
                onRetry={() => setRetry((prev) => prev + 1)}
                onContinue={onInventoryStep1Continue}
                onSwitchToBulkUpload={onSwitchToBulkUpload}
                onDownloadTemplate={downloadSerializationTemplate}
                templateLoading={templateLoading}
              />
            </ModalFormWrapper>

            {/* MED-649: step 2 (hierarchy-driven serial entry) */}
            <ModalWideWrapper show={stage === "inventory-step2"}>
              {inventoryProduct && (
                <InventoryStep2
                  packagingLayers={inventoryProduct.packaging_layers}
                  submitLoading={inventorySubmitLoading}
                  serverErrorBySerial={serverErrorBySerial}
                  onBack={onInventoryStep2Back}
                  onSubmit={onInventorySubmit}
                />
              )}
            </ModalWideWrapper>
          </ModalWrapper>
        </Modal>
        {/* *********** Batch uploading section ************ */}
        <Modal
          isOpen={openBatchUpload}
          close={CloseBatuploadModal}
          title={step === "form" ? "Add Inventory" : "Preview Inventory"}
          subTitle={
            step === "form"
              ? "Upload Batch Serialization Data"
              : "Confirm Inventory Details"
          }
        >
          {step === "form" ? (
            /* ===================== FORM STEP ===================== */
            <div className="sm:w-[40rem] w-[30rem]">
              <div className="mx-10">
                <Select
                  prompt="Select the product"
                  placeholder="Select a product"
                  name="product_id"
                  options={productOptions}
                  fullWidth
                  shortWidth={false}
                  indexLevel={2}
                  returnId
                  formValues={BatchformData}
                  setFormValues={setBatchFormData}
                  updateData={handleExternalUpdate}
                />
              </div>

              <div className="p-3 m-5">
                <div className="mt-2 mb-2 flex justify-end">
                  {/* MED-646: template is generated per product (dynamic
                      per-level serial columns); enabled once a product is
                      selected. Replaces the static Batch-upload-template.xlsx. */}
                  <Button
                    type="button"
                    onClick={() =>
                      downloadSerializationTemplate(BatchformData?.product_id)
                    }
                    disabled={!BatchformData?.product_id || templateLoading}
                  >
                    {templateLoading
                      ? "Preparing template..."
                      : "Download template for this product"}
                  </Button>
                </div>

                <FileUpload onFileSelect={setSelectedFile} />
              </div>

              <div className="my-5 flex justify-center">
                <Button
                  onClick={() => setStep("preview")}
                  type="button"
                  disabled={!selectedFile || !BatchformData}
                >
                  Continue
                </Button>
              </div>
            </div>
          ) : headerMismatch ? (
            /* ============ HEADER-MISMATCH CONFIRMATION (§4.3) ============ */
            <div className="sm:w-[40rem] w-[30rem] mx-auto p-6">
              <h3 className="font-semibold text-gray-800 mb-2">
                Column headers don&apos;t match this product&apos;s template
              </h3>
              <p className="text-sm text-gray-600 mb-3">
                The columns are positionally valid, but their names differ. You
                can upload by position anyway.
              </p>
              <p className="text-sm mb-1">
                <span className="font-medium">Expected:</span>{" "}
                {headerMismatch.expected_columns?.join(", ")}
              </p>
              <p className="text-sm mb-4">
                <span className="font-medium">Got:</span>{" "}
                {headerMismatch.actual_columns?.join(", ")}
              </p>
              <div className="flex justify-between gap-3">
                <Button
                  type="button"
                  secondary
                  onClick={() => setHeaderMismatch(null)}
                >
                  Back
                </Button>
                <Button
                  type="button"
                  onClick={() => onBatchSubmit(true)}
                  disabled={submitLoading}
                >
                  {submitLoading ? "Uploading..." : "Upload by position anyway"}
                </Button>
              </div>
            </div>
          ) : (
            /* ===================== PREVIEW STEP ===================== */
            <PreviewInventory
              data={selectedProduct}
              filePreview={selectedFile}
              packagingTypes={BatchformData?.package_type}
              onSubmit={() => onBatchSubmit(false)}
              onBack={() => setStep("form")}
            />
          )}
        </Modal>
      </AddInventoryWrapper>

      <ActionPopup
        position={position}
        open={showInventoryOptions}
        onClose={() => setShowInventoryOptions(false)}
        actions={InvertoryTypeActions}
      />
    </>
  );
}
